<template>
  <div>
    <div class="d-flex justify-content-center">
      <label
        class="input__label flex-grow-1 flex-shrink-1 margin__r--12"
        :for="id"
      >
        {{ translate(label) }} <span v-if="block.is_required">*</span>
      </label>

      <button
        type="button"
        class="filter flex-shrink-0 link__color--prim link--naked"
        :aria-expanded="is_filter_open"
        :aria-controls="`${id}_filter`"
        :aria-label="is_filter_open ? t('literal.hide_filter') : t('literal.show_filter')"
        @click="is_filter_open = !is_filter_open"
        v-if="allTags.length"
      >
        <fa-icon :icon="is_filter_open ? ['fas', 'filter'] : ['fal', 'filter']" />

        {{ t('literal.filter') }}

        <span
          aria-live="polite"
          aria-atomic="true"
          v-show="tags.length"
        >
          <span aria-hidden="true">({{ tags.length }})</span>

          <span class="sr-only">{{ t('plural.x_active_filters', { x: tags.length }, tags.length) }}</span>
        </span>
      </button>
    </div>

    <div
      :id="`${id}_filter`"
      class="tags d-flex align-items-center input--border input--radius margin__t--6"
      role="region"
      :aria-labelledby="`${id}_tags_label`"
      v-if="is_filter_open"
    >
      <label
        class="color--prim flex-shrink-0 margin__r--6 margin__l--12 cursor--pointer"
        :id="`${id}_tags_label`"
        :for="`${id}_tags`"
      >
        <fa-icon :icon="['fal', 'filter']" />

        <span class="sr-only">{{ t('literal.filter_by_tags') }}</span>
      </label>

      <input-select
        class="flex-grow-1"
        :id="`${id}_tags`"
        :options="tagsOptions"
        :close-on-select="false"
        multiple
        searchable
        deselect-from-dropdown
        v-model="tags"
        @update:model-value="handleFetchBenefitings()"
      />
    </div>

    <input-select
      class="margin__t--12"
      :id="id"
      label="name"
      :options="benefitings"
      :searchable="true"
      :clearable="true"
      :filterable="false"
      :reduce="benefiting => benefiting.id"
      :reduce-attribute="benefiting => benefiting.id"
      :v="validator"
      v-model="benefiting"
      @search="handleChangeSearch"
    >
      <template #selected-option="{ option }">
        {{ model?.name || option?.name }}
      </template>
    </input-select>
  </div>
</template>

<script setup>
import { ref, reactive, computed, inject, onBeforeUnmount, onBeforeMount } from 'vue'
import { storeToRefs } from 'pinia'
import { useNotificationStore } from '@/stores/NotificationStore'
import { useBenefitingStore } from '@/stores/BenefitingStore'
import { useModule } from '@/composables/app/useModule'
import { useProcedure, useProcedureBlockValidator, useProcedureNavigation } from '@/composables/modules/procedures'
import { useI18n } from '@/vendors/i18n'
import { uniqBy as _uniqBy } from 'lodash'
import Benefiting from '@/classes/benefitings/Benefiting'

import InputSelect from '&/atoms/InputSelect'

const { block } = defineProps({ block: { type: Object, required: true } })

const procedure = inject('procedure')
const procedure_execution = inject('procedure_execution')

const benefiting = computed({
  get: () => model.value?.id,
  set: id => model.value = benefitings.value.find(benefiting => benefiting.id === id) || new Benefiting()
})

const { t, translate } = useI18n()
const { postModalNotification } = useNotificationStore()
const { getBenefitings, fetchBenefitings } = useBenefitingStore()
const { tags: allTags } = storeToRefs(useBenefitingStore())
const { id: procedureId, category, targetType } = useProcedure({ procedure })
const { label, model, id } = useModule({ component: block, parent: procedure_execution })
const { validator } = useProcedureBlockValidator({ validations: block.validations, state: benefiting })
const { nextRedirection } = useProcedureNavigation({ procedure, procedure_execution })

const is_filter_open = ref(false)
const timeout = ref(null)
const query = reactive({ tags: [], name: null, procedure_id: procedureId.value })

const { benefitings } = getBenefitings({ query, fetch: false })

const tags = computed({
  get: () => query.tags || [],
  set: tags => query.tags = tags.sort()
})

const tagsOptions = computed(() => _uniqBy(allTags.value.map(tag => ({ label: tag.trim(), value: tag.trim() })).filter(tag => tag.label), 'value'))

const defaultValue = computed(() => {
  if (category.value === 'donation' && targetType.value === 'Project' && procedure.value.project?.benefiting.id) return procedure.value.project.benefiting

  return new Benefiting()
})

const handleFetchBenefitings = async () => {
  try {
    if (!benefitings.value.length) await fetchBenefitings({ query })
  } catch {
    postModalNotification({ title: 'errors.form_initialisation_failed_title', text: 'errors.form_initialisation_failed_text', code: '#001203', error: 'GET Benefitings failed', state: { procedure_id: procedure.value.id, query }, close: nextRedirection })
  }
}

const handleChangeSearch = async (search, loading) => {
  clearTimeout(timeout.value)

  timeout.value = setTimeout(async () => {
    try {
      loading(true)
      query.name = search

      await handleFetchBenefitings()
    } finally {
      loading(false)
    }
  }, 1000)
}

onBeforeMount(async () => {
  await handleFetchBenefitings()

  if (!benefitings.value.length) {
    delete query.procedure_id

    await handleFetchBenefitings()
  }
})

onBeforeUnmount(() => {
  if (!block.isVisible(procedure_execution)) model.value = new Benefiting()
})

if (!benefiting.value) model.value = defaultValue.value
</script>

<style lang="scss" scoped>
.filter {
  font-size: rem(14px);
}

.tags {
  :deep(.v-select) {
    border: 0;
  }
}
</style>
